<script lang="ts" setup>
  import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
  import { ref, watch } from 'vue'

  const props = defineProps({
    currentPage: {
      type: Number,
      required: true
    },
    totalPages: {
      type: Number,
      required: true
    },
    showPrevNextButtons: {
      type: Boolean,
      required: false,
      default: true
    },
    showPageButtons: {
      type: Boolean,
      required: false,
      default: true
    }
  })

  const emit = defineEmits(['updatePage'])

  const inputValue = ref(props.currentPage.toString())

  // Watch for external page changes and update input
  watch(
    () => props.currentPage,
    (newPage) => {
      inputValue.value = newPage.toString()
    }
  )

  const goToPreviousPage = () => {
    if (props.currentPage > 1) {
      emit('updatePage', props.currentPage - 1)
    }
  }

  const goToNextPage = () => {
    if (props.currentPage < props.totalPages) {
      emit('updatePage', props.currentPage + 1)
    }
  }

  const handleInputChange = (event: Event) => {
    const target = event.target as HTMLInputElement
    const value = parseInt(target.value)

    if (!isNaN(value) && value >= 1 && value <= props.totalPages) {
      emit('updatePage', value)
    } else {
      // Reset to current page if invalid
      inputValue.value = props.currentPage.toString()
    }
  }

  const handleInputKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleInputChange(event)
      ;(event.target as HTMLInputElement).blur()
    } else if (event.key === 'Escape') {
      inputValue.value = props.currentPage.toString()
      ;(event.target as HTMLInputElement).blur()
    }
  }

  const handleInputBlur = (event: Event) => {
    handleInputChange(event)
  }
</script>

<template>
  <div
    v-show="totalPages > 1"
    class="flex items-center justify-center gap-3"
  >
    <!-- Previous button -->
    <button
      v-if="showPrevNextButtons"
      :disabled="currentPage === 1"
      class="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-100
        disabled:cursor-not-allowed disabled:opacity-50"
      @click="goToPreviousPage"
    >
      <ChevronLeft class="h-4 w-4" />
    </button>

    <!-- Page input and total -->
    <div class="flex items-center gap-2">
      <input
        v-model="inputValue"
        type="text"
        class="h-8 w-12 rounded-md border border-purple-300 bg-gray-100 px-2 text-center text-sm font-medium
          text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
        @blur="handleInputBlur"
        @keydown="handleInputKeydown"
      />
      <span class="text-sm text-gray-600">of</span>
      <span class="text-sm font-medium text-gray-900">{{ totalPages }}</span>
    </div>

    <!-- Next button -->
    <button
      v-if="showPrevNextButtons"
      :disabled="currentPage === totalPages"
      class="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-100
        disabled:cursor-not-allowed disabled:opacity-50"
      @click="goToNextPage"
    >
      <ChevronRight class="h-4 w-4" />
    </button>
  </div>
</template>
